import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
    Box,
    Button,
    TextField,
    InputAdornment,
    IconButton,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import {
    Visibility,
    Edit,
    Delete,
    Search,
    Add
} from "@mui/icons-material";
import axiosInstance from "../../../Config/axiosInstance";
import "./AllAutovehicles.css";

const SEARCH_DEBOUNCE_MS = 300;
const DEFAULT_PAGE_SIZE = 10;

const useDebounce = (value, delay) => {
    const [debounced, setDebounced] = useState(value);
    useEffect(() => {
        const t = setTimeout(() => setDebounced(value), delay);
        return () => clearTimeout(t);
    }, [value, delay]);
    return debounced;
};

const AllAutovehicles = () => {
    const navigate = useNavigate();

    // state form + permisiuni
    const [searchTerm, setSearchTerm] = useState("");
    const debouncedSearchTerm = useDebounce(searchTerm, SEARCH_DEBOUNCE_MS);

    // Înlocuiește Snackbar-urile MUI cu sistemul de toast din AddAutovehicles
    const [showToast, setShowToast] = useState(false);
    const [toastMessage, setToastMessage] = useState("");
    const [toastType, setToastType] = useState("success"); // "success" sau "error"

    const [canCreate, setCanCreate] = useState(false);
    const [canEdit, setCanEdit] = useState(false);
    const [canView, setCanView] = useState(false);
    const [canDelete, setCanDelete] = useState(false);

    const [vehicles, setVehicles] = useState([]);
    const [drivers, setDrivers] = useState([]);
    const [suppliers, setSuppliers] = useState([]); // Adăugat state pentru suppliers
    const [loading, setLoading] = useState(true);

    // State pentru dialogul de confirmare ștergere
    const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
    const [itemToDelete, setItemToDelete] = useState(null);

    // State pentru dialogul de eroare
    const [errorDialogOpen, setErrorDialogOpen] = useState(false);
    const [deleteErrorMessage, setDeleteErrorMessage] = useState("");

    useEffect(() => {
        const requiredPath = "/all-autovehicles";
        const storedPermissions = JSON.parse(localStorage.getItem("permissions"));
        const pages = storedPermissions?.pages ?? [];

        const checkPermission = (key) =>
            pages.some((p) => {
                try {
                    return new URL(p.url).pathname === requiredPath && p[key] === true;
                } catch {
                    return false;
                }
            });

        setCanCreate(checkPermission("can_create"));
        setCanEdit(checkPermission("can_edit"));
        setCanView(checkPermission("can_view"));
        setCanDelete(checkPermission("can_delete"));
    }, []);

    useEffect(() => {
        axiosInstance
            .get("/employees/")
            .then((res) => setDrivers(res.data))
            .catch(() => console.error("❌ Failed to load employees"));
    }, []);

    // Încărcare supplier-i
    useEffect(() => {
        axiosInstance
            .get("/suppliers/") // Presupun că endpoint-ul pentru supplier-i este /suppliers/
            .then((res) => setSuppliers(res.data))
            .catch(() => console.error("❌ Failed to load suppliers"));
    }, []);

    //returneaza numele soferului dupa id
    const getDriverName = useCallback(
        (id) => {
            const d = drivers.find((x) => x.id === id);
            return d ? `${d.first_name} ${d.last_name}` : "-";
        },
        [drivers]
    );

    //returneaza numele supplier-ului dupa id
    const getSupplierName = useCallback(
        (id) => {
            const s = suppliers.find((x) => x.id === id);
            return s ? s.name : "-"; // Presupun că supplier-ul are un câmp "name"
        },
        [suppliers]
    );

    //fetch autovehicule din backend
    const fetchVehicles = useCallback(async () => {
        try {
            setLoading(true);
            const res = await axiosInstance.get("/all-autovehicles/");

            const updated = res.data.map((v, idx) => ({
                ...v,
                id: v.id ?? idx,
                driver_name: getDriverName(v.driver),
                supplier_name: getSupplierName(v.supplier), // Adăugat numele supplier-ului
                // Asigură că statusul este prezent
                status: v.status || "Open"
            }));

            setVehicles(updated);
        } catch (err) {
            console.error("❌ fetchVehicles error:", err);
            showToastMessage("Error loading vehicles", "error");
        } finally {
            setLoading(false);
        }
    }, [getDriverName, getSupplierName]);

    useEffect(() => {
        fetchVehicles();
    }, [fetchVehicles]);

    //filtrare dupa search
    const filteredRows = useMemo(() => {
        let list = [...vehicles];

        if (debouncedSearchTerm) {
            const s = debouncedSearchTerm.toLowerCase();
            list = list.filter((v) => {
                return (
                    v.registration_number?.toLowerCase().includes(s) ||
                    v.auto?.toLowerCase().includes(s) ||
                    v.auto_type?.toLowerCase().includes(s) ||
                    v.driver_name?.toLowerCase().includes(s) ||
                    v.supplier_name?.toLowerCase().includes(s) || // Adăugat căutare după supplier
                    v.expire?.toLowerCase().includes(s) ||
                    v.status?.toLowerCase().includes(s)
                );
            });
        }

        // Sortează: Open înainte, apoi Closed
        list.sort((a, b) => {
            if (a.status === "Open" && b.status !== "Open") return -1;
            if (a.status !== "Open" && b.status === "Open") return 1;
            return 0;
        });

        return list;
    }, [vehicles, debouncedSearchTerm]);

    //deschide pagina de view
    const handleViewDetails = useCallback(
        (row) => {
            if (!canView) return;
            navigate(`/all-autovehicles/view/${row.id}`);
        },
        [canView, navigate]
    );

    const handleEditVehicle = useCallback(
        (vehicle) => {
            if (!canEdit) return;
            navigate(`/all-autovehicles/edit/${vehicle.id}`);
        },
        [canEdit, navigate]
    );

    // Funcție pentru ștergere
    const handleDeleteClick = (row) => {
        setItemToDelete(row);
        setOpenDeleteDialog(true);
    };

    // Adaugă acest state după celelalte state-uri (la linia ~52)
    const [isDeleting, setIsDeleting] = useState(false);

    // Modifică funcția handleDelete astfel:
    const handleDelete = async () => {
        setIsDeleting(true);
        try {
            await axiosInstance.delete(`/all-autovehicles/${itemToDelete.id}/`);

            // Actualizează lista local
            setVehicles(prev => prev.filter(v => v.id !== itemToDelete.id));

            // Închide dialogul
            setOpenDeleteDialog(false);

            // Arată mesaj de succes folosind sistemul de toast
            showToastMessage("Vehicle deleted successfully!", "success");

        } catch (error) {
            console.error("❌ Delete error:", error);

            const message = error.response?.data?.error ||
                error.response?.data?.detail ||
                "An unexpected error occurred while deleting.";

            setDeleteErrorMessage(message);
            setOpenDeleteDialog(false);
            setErrorDialogOpen(true);
        } finally {
            setIsDeleting(false);
        }
    };

    // Modifică funcția de închidere a dialogului pentru a reseta isDeleting:
    const handleCloseDeleteDialog = () => {
        setOpenDeleteDialog(false);
        setIsDeleting(false); // Asigură-te că starea este resetată
    };

    // În partea de JSX, actualizează Dialog-ul de confirmare:
    <Dialog
        open={openDeleteDialog}
        onClose={handleCloseDeleteDialog} // Folosește noua funcție
        PaperProps={{
            sx: {
                borderRadius: '10px',
                px: 2,
                py: 1,
                boxShadow: '0px 8px 24px rgba(0, 0, 0, 0.2)',
                minWidth: 200,
            },
        }}
    >
        <DialogTitle
            sx={{
                textAlign: 'left',
                fontWeight: 600,
                fontSize: '1.25rem',
                color: '#333',
            }}
        >
            Confirm Delete
        </DialogTitle>

        <DialogContent>
            <DialogContentText
                sx={{
                    textAlign: 'left',
                    fontSize: '0.95rem',
                    color: '#666',
                    px: 0,
                }}
            >
                Are you sure you want to delete vehicle <strong>{itemToDelete?.registration_number}</strong> ({itemToDelete?.auto})?
            </DialogContentText>
        </DialogContent>

        <DialogActions
            sx={{
                justifyContent: 'center',
                gap: 2,
                flexWrap: 'wrap',
            }}
        >
            <Button
                onClick={handleCloseDeleteDialog}
                variant="contained"
                disabled={isDeleting} // Dezactivează și butonul Cancel în timpul ștergerii
                sx={{
                    backgroundColor: '#e0e0e0',
                    color: '#333',
                    fontWeight: 500,
                    textTransform: 'none',
                    minWidth: { xs: '100%', sm: 100 },
                    mb: { xs: 1, sm: 0 },
                    '&:hover': { backgroundColor: '#d5d5d5' },
                    '&.Mui-disabled': {
                        backgroundColor: '#f5f5f5',
                        color: '#bdbdbd',
                    }
                }}
            >
                Cancel
            </Button>
            <Button
                onClick={handleDelete}
                disabled={isDeleting} // Dezactivează butonul când ștergerea este în curs
                variant="contained"
                sx={{
                    backgroundColor: isDeleting ? '#ff6b6b' : '#d32f2f', // Schimbă culoarea pentru feedback vizual
                    color: '#fff',
                    fontWeight: 600,
                    textTransform: 'none',
                    minWidth: { xs: '100%', sm: 100 },
                    ml: { xs: 0, sm: 2 },
                    '&:hover': {
                        backgroundColor: isDeleting ? '#ff6b6b' : '#b71c1c'
                    },
                    '&.Mui-disabled': {
                        backgroundColor: '#ffcdd2',
                        color: '#ffebee',
                    }
                }}
            >
                {isDeleting ? (
                    <>
                        {/* Opțional: adaugă un indicator de încărcare */}
                        <span style={{ marginRight: '8px' }}>⏳</span>
                        Deleting...
                    </>
                ) : (
                    'Delete'
                )}
            </Button>
        </DialogActions>
    </Dialog>

    // Funcție pentru afișarea toast message (similară cu cea din AddAutovehicles)
    const showToastMessage = (message, type = "success") => {
        setToastMessage(message);
        setToastType(type);
        setShowToast(true);
        setTimeout(() => setShowToast(false), 3000);
    };

    //verifica statusul expirarii
    const getExpirationStatus = (dateString) => {
        if (!dateString) return "expired";

        const today = new Date();
        const date = new Date(dateString);
        const diffDays = Math.ceil((date - today) / (1000 * 60 * 60 * 24));

        if (diffDays < 0) return "expired";
        if (diffDays <= 5) return "warning";
        return "active";
    };

    //definire coloane tabel
    const columns = useMemo(() => {
        const base = [
            { field: "registration_number", headerName: "Registration number", flex: 1, minWidth: 140 },
            { field: "auto", headerName: "Auto", flex: 1, minWidth: 130 },
            { field: "auto_type", headerName: "Type", flex: 1, minWidth: 130 },
            { field: "driver_name", headerName: "Driver", flex: 1.2, minWidth: 150 },
            {
                field: "year",
                headerName: "Year",
                flex: 0.6,
                minWidth: 90,
                renderCell: (p) => (p.value ? new Date(p.value).getFullYear() : "–"),
            },
            // Adăugat coloana pentru supplier înainte de status
            {
                field: "supplier_name",
                headerName: "Supplier",
                flex: 1,
                minWidth: 130,
            },
            {
                field: "status",
                headerName: "Status",
                flex: 0.7,
                minWidth: 100,
                renderCell: (params) => {
                    const status = params.value || "Open";
                    return (
                        <div style={{ display: "flex", alignItems: "center" }}>
                            <span
                                className={`status-dot status-${status.toLowerCase()}`}
                                style={{
                                    backgroundColor: status === "Open" ? "#4CAF50" : "#f44336",
                                    marginRight: "6px"
                                }}
                            ></span>
                            {status}
                        </div>
                    );
                },
                sortComparator: (v1, v2) => {
                    // Sortează: Open înainte, apoi Closed
                    if (v1 === "Open" && v2 !== "Open") return -1;
                    if (v1 !== "Open" && v2 === "Open") return 1;
                    return 0;
                }
            },
            {
                field: "expire",
                headerName: "Contract",
                flex: 0.8,
                minWidth: 120,
                renderCell: (params) => {
                    const status = getExpirationStatus(params.value);
                    return (
                        <div style={{ display: "flex", alignItems: "center" }}>
                            <span className={`status-dot status-${status}`}></span>
                            {params.value ? new Date(params.value).toLocaleDateString("ro-RO") : "–"}
                        </div>
                    );
                }
            },
            {
                field: "RCA",
                headerName: "RCA",
                flex: 0.7,
                minWidth: 110,
                renderCell: (params) => {
                    const status = getExpirationStatus(params.value);
                    return (
                        <div style={{ display: "flex", alignItems: "center" }}>
                            <span className={`status-dot status-${status}`}></span>
                            {params.value ? new Date(params.value).toLocaleDateString("ro-RO") : "–"}
                        </div>
                    );
                }
            },
            {
                field: "ITP",
                headerName: "ITP",
                flex: 0.7,
                minWidth: 110,
                renderCell: (params) => {
                    const status = getExpirationStatus(params.value);
                    return (
                        <div style={{ display: "flex", alignItems: "center" }}>
                            <span className={`status-dot status-${status}`}></span>
                            {params.value ? new Date(params.value).toLocaleDateString("ro-RO") : "–"}
                        </div>
                    );
                }
            },
            {
                field: "E_Vignette",
                headerName: "Vignette",
                flex: 0.8,
                minWidth: 120,
                renderCell: (params) => {
                    const status = getExpirationStatus(params.value);
                    return (
                        <div style={{ display: "flex", alignItems: "center" }}>
                            <span className={`status-dot status-${status}`}></span>
                            {params.value ? new Date(params.value).toLocaleDateString("ro-RO") : "–"}
                        </div>
                    );
                }
            },
        ];

        // Adaugă coloana de acțiuni doar dacă există permisiuni
        if (canView || canEdit || canDelete) {
            base.push({
                field: "action",
                headerName: "Action",
                width: 150,
                renderCell: (params) => (
                    <div style={{ display: "flex", gap: "6px" }}>
                        {canView && (
                            <IconButton
                                onClick={() => handleViewDetails(params.row)}
                                sx={{ color: "#093d71ff" }}
                                size="small"
                            >
                                <Visibility fontSize="small" />
                            </IconButton>
                        )}
                        {canEdit && (
                            <IconButton
                                onClick={() => handleEditVehicle(params.row)}
                                sx={{ color: "#1976d2" }}
                                size="small"
                            >
                                <Edit fontSize="small" />
                            </IconButton>
                        )}
                        {canDelete && (
                            <IconButton
                                onClick={() => handleDeleteClick(params.row)}
                                sx={{ color: "#d32f2f" }}
                                size="small"
                            >
                                <Delete fontSize="small" />
                            </IconButton>
                        )}
                    </div>
                ),
                sortable: false,
                disableColumnMenu: true,
            });
        }

        return base;
    }, [canView, canEdit, canDelete, handleViewDetails, handleEditVehicle]);

    return (
        <div className="page-allautovehicles">
            {/* Toast Message - EXACT CA ÎN AddAutovehicles.js */}
            {showToast && (
                <div className={`global-toast ${toastType}`}>
                    {toastType === "success" ? "✔ " : "✗ "}
                    {toastMessage}
                </div>
            )}

            <div className="vehicles-page">

                {/*toolbar + search + new buton*/}
                <Box className="vehicles-toolbar">
                    <h2 className="title">FLEET MANAGEMENT</h2>

                    <Box className="vehicles-toolbar-right">
                        <TextField
                            size="small"
                            placeholder="Search..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="search-input"
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <Search sx={{ color: "#424242" }} />
                                    </InputAdornment>
                                ),
                            }}
                        />

                        {canCreate && (
                            <Button
                                variant="contained"
                                startIcon={<Add />}
                                onClick={() => navigate("/all-autovehicles/add")}
                                className="new-btn"
                            >
                                NEW
                            </Button>
                        )}
                    </Box>
                </Box>

                <div className="table-container">
                    <DataGrid
                        rows={filteredRows}
                        columns={columns}
                        loading={loading}
                        disableRowSelectionOnClick
                        initialState={{
                            pagination: {
                                paginationModel: { pageSize: DEFAULT_PAGE_SIZE, page: 0 },
                            },
                            sorting: {
                                sortModel: [{ field: 'status', sort: 'asc' }] // Sortare implicită pe status
                            }
                        }}
                        sx={{
                            "& .MuiDataGrid-cell": {
                                whiteSpace: "normal",
                                wordBreak: "break-word",
                                lineHeight: "1",
                                display: "flex",
                                alignItems: "center",
                            },
                            "& .MuiDataGrid-columnHeaders": {
                                whiteSpace: "normal",
                            },
                            "& .MuiDataGrid-cell:focus": {
                                outline: "none",
                            },
                            "& .MuiDataGrid-cell:focus-within": {
                                outline: "none",
                            },
                            "& .Mui-selected:hover": {
                                backgroundColor: "transparent !important",
                            },
                        }}
                        pageSizeOptions={[10, 12, 20, 25]}
                    />
                </div>
            </div>

            {/* Dialog de confirmare ștergere */}
            <Dialog
                open={openDeleteDialog}
                onClose={() => setOpenDeleteDialog(false)}
                PaperProps={{
                    sx: {
                        borderRadius: '10px',
                        px: 2,
                        py: 1,
                        boxShadow: '0px 8px 24px rgba(0, 0, 0, 0.2)',
                        minWidth: 200,
                    },
                }}
            >
                <DialogTitle
                    sx={{
                        textAlign: 'left',
                        fontWeight: 600,
                        fontSize: '1.25rem',
                        color: '#333',
                    }}
                >
                    Confirm Delete
                </DialogTitle>

                <DialogContent>
                    <DialogContentText
                        sx={{
                            textAlign: 'left',
                            fontSize: '0.95rem',
                            color: '#666',
                            px: 0,
                        }}
                    >
                        Are you sure you want to delete vehicle <strong>{itemToDelete?.registration_number}</strong> ({itemToDelete?.auto})?
                    </DialogContentText>
                </DialogContent>

                <DialogActions
                    sx={{
                        justifyContent: 'center',
                        gap: 2,
                        flexWrap: 'wrap',
                    }}
                >
                    <Button
                        onClick={() => setOpenDeleteDialog(false)}
                        variant="contained"
                        sx={{
                            backgroundColor: '#e0e0e0',
                            color: '#333',
                            fontWeight: 500,
                            textTransform: 'none',
                            minWidth: { xs: '100%', sm: 100 },
                            mb: { xs: 1, sm: 0 },
                            '&:hover': { backgroundColor: '#d5d5d5' },
                        }}
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={handleDelete}
                        variant="contained"
                        sx={{
                            backgroundColor: '#d32f2f',
                            color: '#fff',
                            fontWeight: 600,
                            textTransform: 'none',
                            minWidth: { xs: '100%', sm: 100 },
                            ml: { xs: 0, sm: 2 },
                            '&:hover': { backgroundColor: '#b71c1c' },
                        }}
                    >
                        Delete
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Dialog de eroare */}
            <Dialog
                open={errorDialogOpen}
                onClose={() => setErrorDialogOpen(false)}
                PaperProps={{
                    sx: {
                        borderRadius: '10px',
                        px: 2,
                        py: 1,
                        boxShadow: '0px 8px 24px rgba(0, 0, 0, 0.2)',
                    },
                }}
            >
                <DialogTitle
                    sx={{
                        textAlign: 'left',
                        fontWeight: 600,
                        fontSize: '1.25rem',
                        color: '#d32f2f',
                    }}
                >
                    Delete Failed
                </DialogTitle>
                <DialogContent>
                    <DialogContentText
                        sx={{
                            textAlign: 'left',
                            fontSize: '0.95rem',
                            color: '#666',
                        }}
                    >
                        {deleteErrorMessage}
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={() => setErrorDialogOpen(false)}
                        variant="contained"
                        sx={{
                            backgroundColor: '#1976d2',
                            color: '#fff',
                            textTransform: 'none',
                        }}
                    >
                        Close
                    </Button>
                </DialogActions>
            </Dialog>
        </div>
    );
};

export default AllAutovehicles;